<script lang="ts">
	import Basic from '../templates/Basic.svelte'
	import { setContext } from 'svelte'
	import { DatePickerMode } from '../types/datepicker'
	import { inputFieldFormats } from '../utils/formatters'
	import DatePickerInput from '../atoms/DatePickerInput.svelte'
	import { t } from '../../i18n/i18n'
	import {
		fetchMailProtocol,
		getParamsFromUrlOrDefault,
		updateUrlWithSearchParams
	} from './MailProtocol.controller'
	import MailProtocolEntry from './MailProtocolEntry.svelte'
	import InputField from '../molecules/InputField.svelte'
	import dayjs from 'dayjs'
	import AutocompleteInputField from '../autocomplete/AutocompleteInputField.svelte'
	import Skeleton from 'svelte-skeleton/Skeleton.svelte'
	import { scrollToTop } from '../utils/misc'
	import { MailProtocolStatus } from './MailProtocol.model'

	setContext('useMaterialDesign', true)
	const sentSuccessfulOptions = [
		{ label: $t('UI.mail.protocol.status.delivered'), value: MailProtocolStatus.DELIVERED },
		{ label: $t('UI.mail.protocol.status.pending'), value: MailProtocolStatus.PENDING },
		{ label: $t('UI.mail.protocol.status.failed'), value: MailProtocolStatus.FAILED }
	]
	const MAX_MAIL_PROTOCOL_DISPLAY = 250

	let { timeframe, searchTerm, status } = getParamsFromUrlOrDefault(
		new URLSearchParams(window.location.search)
	)

	let debouncedSearchTerm = null

	function updateSearch() {
		updateUrlWithSearchParams(searchTerm, timeframe, status, window)
		debouncedSearchTerm = searchTerm
	}
</script>

<Basic showLoadingAnimation={false} maxWidth="2160px" backgroundColor="#f5f5f5">
	<div class="mail-protocol mail-protocol__background">
		<div class="mail-protocol__header">
			<div class="mail-protocol__header--info">
				<h1>{$t('UI.mail.protocol.mailProtocol')}</h1>
				<h1>{dayjs().format('DD.MM.YYYY')}</h1>
			</div>
		</div>

		<div class="mail-protocol__filter">
			<div>
				<InputField
					bind:value={searchTerm}
					label={$t('UI.mail.protocol.search')}
					placeholder={$t('UI.mail.protocol.search.ph')}
					format={inputFieldFormats.FULL}
					on:clear={updateSearch}
					on:change={updateSearch}
				/>
			</div>
			<div>
				<DatePickerInput
					bind:date={timeframe}
					label={$t('UI.Dashboard.mainTimeframe.label')}
					mode={DatePickerMode.RANGE}
					format={inputFieldFormats.FULL}
					on:clear={() => updateUrlWithSearchParams(searchTerm, timeframe, status, window)}
					on:change={() => updateUrlWithSearchParams(searchTerm, timeframe, status, window)}
				/>
			</div>
			<div>
				<AutocompleteInputField
					bind:value={status}
					label={$t('UI.mail.protocol.sentSuccessful')}
					options={sentSuccessfulOptions}
					onClear={() => updateUrlWithSearchParams(searchTerm, timeframe, status, window)}
					onChange={() => updateUrlWithSearchParams(searchTerm, timeframe, status, window)}
				/>
			</div>
		</div>

		<div class="mail-protocol-table">
			<div class="mail-protocol-table-header">
				<div class="mail-protocol-table-header-cell">
					<h3>{$t('UI.mail.protocol.status')}</h3>
				</div>
				<div class="mail-protocol-table-header-cell">
					<h3>{$t('UI.mail.protocol.subject')}</h3>
				</div>
				<div class="mail-protocol-table-header-cell">
					<h3>{$t('UI.mail.protocol.sender')}</h3>
				</div>
				<div class="mail-protocol-table-header-cell">
					<h3>{$t('UI.mail.protocol.recipient')}</h3>
				</div>
				<div class="mail-protocol-table-header-cell">
					<h3>Aktion</h3>
				</div>
			</div>
			<div class="mail-protocol-table-content">
				{#key timeframe || debouncedSearchTerm || status}
					{#await fetchMailProtocol(debouncedSearchTerm, timeframe, status)}
						{#each Array(12) as _}
							<div class="mail-protocol__skeleton" style="grid-column: 1/2;">
								<Skeleton width="80%" height="1.5rem" />
								<Skeleton width="75%" height="0.875rem" />
								<Skeleton width="75%" height="0.875rem" />
							</div>
							<div class="mail-protocol__skeleton" style="grid-column: 2/3;">
								<Skeleton width="90%" height="1.5rem" />
							</div>
							<div class="mail-protocol__skeleton" style="grid-column: 3/4;">
								<Skeleton width="80%" height="1rem" />
								<Skeleton width="80%" height="1rem" />
							</div>
							<div class="mail-protocol__skeleton" style="grid-column: 4/5;">
								<Skeleton width="80%" height="1rem" />
								<Skeleton width="80%" height="1rem" />
							</div>
							<div class="mail-protocol__skeleton" style="grid-column: 5/-1;">
								<Skeleton width="75%" height="1rem" />
							</div>
						{/each}
						<div class="mail-protocol__skeleton--footer">
							<Skeleton width="100%" height="1.5rem" />
						</div>
					{:then data}
						{@const entries = data?.sort((a, b) => b.sendUnixTimestamp - a.sendUnixTimestamp) || []}
						{#each entries as protocolData}
							<MailProtocolEntry {protocolData} />
						{/each}
						<div class="mail-protocol-table__footer">
							<span>
								{#if data?.length >= MAX_MAIL_PROTOCOL_DISPLAY}
									Maximale Anzahl von <b>{MAX_MAIL_PROTOCOL_DISPLAY}</b> Einträgen erreicht. Passe
									die Suchkriterien an, um andere Ergebnisse zu erhalten.
									<a class="to-top" on:click={scrollToTop}>Nach oben</a>
								{:else if data?.length > 0}
									<b>{data.length}</b> Einträge gefunden.
									<a class="to-top" on:click={scrollToTop}>Nach oben</a>
								{:else if data?.length === 0}
									Keine Einträge passend zu den Suchkriterien gefunden.
								{/if}
							</span>
						</div>
					{:catch error}
						<p style="grid-column: 1/-1; margin-top: 1rem;">
							Error: {error?.message || 'Mail-Protokoll konnte nicht geladen werden.'}
						</p>
					{/await}
				{/key}
			</div>
		</div>
	</div>
</Basic>

<style lang="scss">
	@use '$styles/global';
	@use '$styles/breakpoints';

	/* set background for body such that success message does not have white background when showing */
	body {
		--bs-body-bg: #f5f5f5;
	}

	.mail-protocol {
		@include global.flex-col($justify: flex-start);
	}

	.mail-protocol__header {
		width: 100%;

		@include global.flex-col;
	}

	.mail-protocol__header--info {
		width: 100%;

		@include global.flex-row(0.75rem, $justify: space-between);

		margin-bottom: 1.875rem;

		h1 {
			color: global.$primaryGreen;
			font-size: 0.875rem;
			margin-bottom: 0;
		}
	}

	.mail-protocol__filter {
		@include global.flex-col(0.625rem, $justify: flex-start);

		width: 100%;
		margin-bottom: 0.875rem;

		& > div {
			width: 100%;
		}

		& :global(.material-label.notEmpty .material-text),
		& :global(.material-label.isFocused .material-text),
		& :global(.input-field-focus.date-time-field.notEmpty ~ .material-label .material-text),
		& :global(input.notEmpty ~ label.material-label .material-text) {
			background: linear-gradient(to bottom, #f5f5f5, white);
		}
	}

	.mail-protocol-table {
		display: grid;
		grid-template-columns: 1fr 2fr 2fr 2fr 1fr;
		column-gap: 1.25rem;
		background-color: white;
		border-radius: 1rem;
		padding: 0.5rem 1.25rem;
		width: 100%;
		overflow-x: auto;
	}

	.mail-protocol-table-header {
		display: grid;
		grid-template-columns: subgrid;
		grid-template-rows: 40px;
		grid-column: 1 / -1;
		border-bottom: 1px solid global.$primaryGreen;
		padding-left: 0.25rem;
		padding-right: 0.25rem;
	}

	.mail-protocol-table-header-cell {
		place-self: center start;

		& h3 {
			margin: 0;
		}
	}

	.mail-protocol__skeleton {
		margin-top: 1rem;

		@include global.flex-col(0.125rem, $alignment: flex-start);
	}

	.mail-protocol__skeleton--footer {
		@extend .mail-protocol__skeleton;

		grid-column: 1/-1;
		align-items: center;
		width: 33%;
		justify-self: center;
		min-width: 300px;
	}

	.mail-protocol-table-content {
		display: grid;
		grid-template-columns: subgrid;
		grid-column: 1 / -1;
		padding-left: 0.25rem;
		padding-right: 0.25rem;
	}

	.mail-protocol-table__footer {
		@include global.flex-row(0.625rem, $justify: center);

		width: 100%;
		grid-column: 1 / -1;
		margin-top: 1.5rem;
	}

	a.to-top {
		text-decoration: none;
		cursor: pointer;
		font-weight: bold;
	}

	@media screen and (min-width: breakpoints.$large) {
		.mail-protocol-table {
			padding: 2rem 6rem;
		}
	}

	@media screen and (min-width: breakpoints.$small) {
		.mail-protocol-table {
			padding: 2rem 4rem;
		}

		.mail-protocol__header--info {
			h1 {
				color: global.$primaryGreen;
				font-size: 1.25rem;
			}
		}

		.mail-protocol__filter {
			margin-bottom: 1.25rem;

			@include global.flex-row(0.625rem, $justify: flex-start);

			& > div {
				width: 100%;
				max-width: 255px; /* the single dropdown fields should not be longer than 255px */
			}
		}
	}
</style>
